import { z } from "zod";
import { BaseHttpClient, baseOk, ApiOk } from "./base";

// ---- Schemas ----
export const mediaUploadResponse = baseOk(
  z.object({
    id: z.string(),
    variants: z.array(z.string()).default([]),
    url: z.string().url().or(z.string()),
    type: z.enum(["image", "video"]),
    playback: z
      .object({ hls: z.string().optional(), dash: z.string().optional() })
      .optional(),
  }),
);
export type MediaUploadData = z.infer<typeof mediaUploadResponse>["data"];
export type MediaUploadResult = ApiOk<MediaUploadData>;

export const mediaDeleteResponse = baseOk(
  z.object({ deleted: z.boolean(), id: z.string() }),
);
export type MediaDeleteResult = ApiOk<{ deleted: boolean; id: string }>;

// ---- Inputs ----
export interface MediaUploadInput {
  filename: string;
  filetype: string; // MIME type
  base64String: string; // base64 without data: prefix
  previousImageUrl?: string;
  previousImageId?: string;
}
export interface MediaDeleteInput {
  id: string;
  type?: "image" | "video";
}

export const mediaUploadPublicResponse = baseOk(z.object({ url: z.string() }));
export type MediaUploadPublicData = z.infer<
  typeof mediaUploadPublicResponse
>["data"];
export type MediaUploadPublicResult = ApiOk<MediaUploadPublicData>;

export interface MediaUploadPublicInput {
  filename: string;
  filetype: string;
  base64String: string;
}

export class MediaClient extends BaseHttpClient {
  upload(input: MediaUploadInput): Promise<MediaUploadResult> {
    return this.post("/v1/media/upload", input, mediaUploadResponse);
  }

  /** Public (unauthenticated) image upload for service-request forms. */
  uploadPublic(
    input: MediaUploadPublicInput,
  ): Promise<MediaUploadPublicResult> {
    return this.post(
      "/v1/media/upload-public",
      input,
      mediaUploadPublicResponse,
    );
  }

  /**
   * @deprecated Use domain-specific media delete endpoints when available; avoid new usage of this generic delete route.
   */
  delete(input: MediaDeleteInput): Promise<MediaDeleteResult> {
    return this.post("/v1/media/delete", input, mediaDeleteResponse);
  }
}
